import is from '@sindresorhus/is';
import type { Options } from 'nuqs';
import { parseAsNativeArrayOf, parseAsString, throttle, useQueryState } from 'nuqs';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { useCallback, useMemo } from 'react';

import { commentsGroupCloseSlotKeys, commentsGroupInsertIndex, orderUrlTilesWithCommentTile } from './comments-group';
import { parseUrlTileValues } from './parse-url-tiles';
import { findUrlTileDefinition, serializeUrlTilePayloads } from './serialize-url-tiles';
import type { ParsedUrlTile, UrlTileDefinition, UrlTileMutationOptions, UrlTilePayload } from './types';
import { URL_TILE_SEARCH_PARAM } from './types';
import { UrlTileContext } from './use-url-tiles';

const URL_TILE_URL_SYNC_DELAY_IN_MS = 200;
export const urlTileValuesParser = parseAsNativeArrayOf(parseAsString);
export const urlTileQueryOptions: Options = {
    limitUrlUpdates: throttle(URL_TILE_URL_SYNC_DELAY_IN_MS),
    shallow: true,
};

const getPayloadSlotKey = (payload: UrlTilePayload, definitions: readonly UrlTileDefinition[]): string | undefined =>
    findUrlTileDefinition(payload, definitions)?.slotKeyForPayload(payload);

const replaceOrAppendTile = (
    tiles: readonly ParsedUrlTile[],
    payload: UrlTilePayload,
    definitions: readonly UrlTileDefinition[],
): readonly UrlTilePayload[] => {
    const slotKey = getPayloadSlotKey(payload, definitions);

    if (is.undefined(slotKey)) {
        return tiles.map((tile) => tile.payload);
    }

    const existingIndex = tiles.findIndex((tile) => tile.slotKey === slotKey);
    const payloads = tiles.map((tile) => tile.payload);

    if (existingIndex >= 0) {
        return payloads.map((current, index) => (index === existingIndex ? payload : current));
    }

    // A connected child joins its group block so the synthesized comment tile — which sits
    // after the last group member — stays at the right edge of the group.
    const insertIndex = commentsGroupInsertIndex(tiles, payload, definitions);

    return is.undefined(insertIndex)
        ? [...payloads, payload]
        : [...payloads.slice(0, insertIndex), payload, ...payloads.slice(insertIndex)];
};

const parseUrlTilePayloads = (
    payloads: readonly UrlTilePayload[],
    definitions: readonly UrlTileDefinition[],
): readonly ParsedUrlTile[] => {
    return parseUrlTileValues(serializeUrlTilePayloads(payloads, definitions), definitions);
};

const mutationOptionsToQueryOptions = (
    options: UrlTileMutationOptions | undefined,
    defaultReplace: boolean,
): Options => ({
    ...urlTileQueryOptions,
    history: (options?.replace ?? defaultReplace) ? 'replace' : 'push',
});

export const UrlTileProvider: FunctionComponent<
    PropsWithChildren<{
        readonly definitions: readonly UrlTileDefinition[];
        readonly onUnavailableTilePayload?: (payload: UrlTilePayload) => void;
    }>
> = ({ children, definitions, onUnavailableTilePayload }) => {
    const [tileValues, setTileValues] = useQueryState(
        URL_TILE_SEARCH_PARAM,
        urlTileValuesParser.withOptions(urlTileQueryOptions),
    );

    const tiles = useMemo(() => parseUrlTileValues(tileValues ?? [], definitions), [definitions, tileValues]);

    const mutateTiles = useCallback(
        (
            updatePayloads: (currentTiles: readonly ParsedUrlTile[]) => readonly UrlTilePayload[],
            options: UrlTileMutationOptions | undefined,
            defaultReplace: boolean,
        ) => {
            void setTileValues(
                (currentValues) => {
                    const currentTiles = parseUrlTileValues(currentValues ?? [], definitions);
                    const nextValues = parseUrlTilePayloads(updatePayloads(currentTiles), definitions).map(
                        (tile) => tile.value,
                    );

                    return is.emptyArray(nextValues) ? null : nextValues;
                },
                mutationOptionsToQueryOptions(options, defaultReplace),
            );
        },
        [definitions, setTileValues],
    );

    const hasDefinitionForPayload = useCallback(
        (payload: UrlTilePayload) => {
            if (!is.undefined(findUrlTileDefinition(payload, definitions))) {
                return true;
            }

            onUnavailableTilePayload?.(payload);

            return false;
        },
        [definitions, onUnavailableTilePayload],
    );

    const openTile = useCallback(
        (payload: UrlTilePayload, options?: UrlTileMutationOptions) => {
            if (!hasDefinitionForPayload(payload)) {
                return;
            }

            mutateTiles((currentTiles) => replaceOrAppendTile(currentTiles, payload, definitions), options, false);
        },
        [definitions, hasDefinitionForPayload, mutateTiles],
    );

    const updateTile = useCallback(
        (payload: UrlTilePayload, options?: UrlTileMutationOptions) => {
            if (!hasDefinitionForPayload(payload)) {
                return;
            }

            mutateTiles((currentTiles) => replaceOrAppendTile(currentTiles, payload, definitions), options, true);
        },
        [definitions, hasDefinitionForPayload, mutateTiles],
    );

    const closeTile = useCallback(
        (slotKey: string, options?: UrlTileMutationOptions) => {
            mutateTiles(
                (currentTiles) => {
                    const closedSlotKeys = commentsGroupCloseSlotKeys(currentTiles, slotKey);

                    return currentTiles.filter((tile) => !closedSlotKeys.has(tile.slotKey)).map((tile) => tile.payload);
                },
                options,
                true,
            );
        },
        [mutateTiles],
    );

    const orderedTiles = useMemo(() => orderUrlTilesWithCommentTile(tiles), [tiles]);

    const value = useMemo(
        () => ({
            closeTile,
            openTile,
            orderedTiles,
            tiles,
            updateTile,
        }),
        [closeTile, openTile, orderedTiles, tiles, updateTile],
    );

    return <UrlTileContext.Provider value={value}>{children}</UrlTileContext.Provider>;
};
